/* C5 · PersonaStore — single source of truth for the persona system.
   Consent-gated preference: jp_persona_state · body class: persona-<id>
   event: persona:changed (detail: { persona })
   Click handling is event-delegated so injected UI (drawer grid) just works. */
(() => {
  if (window.__jpPersonaInit) return;
  window.__jpPersonaInit = true;
  const KEY = 'jp_persona_state';
  const VALID = ['all', 'salon', 'barber', 'student'];
  const privacy = () => window.JapanScissors && window.JapanScissors.privacy;
  let LABELS = { all: 'All', salon: 'Salon', barber: 'Barber', student: 'Student' };
  try {
    const el = document.querySelector('script[data-jp-persona-labels]');
    if (el) LABELS = Object.assign(LABELS, JSON.parse(el.textContent));
  } catch {}
  /* per-persona "tuned" copy (header JSON) + cost-per-cut presets */
  let TUNED = {};
  try {
    const t = document.querySelector('script[data-jp-persona-tuned]');
    if (t) TUNED = JSON.parse(t.textContent);
  } catch {}
  let SIZE = {};
  try {
    const s = document.querySelector('script[data-jp-persona-size]');
    if (s) SIZE = JSON.parse(s.textContent);
  } catch {}
  let TOAST = null;
  try {
    const t2 = document.querySelector('script[data-jp-persona-toast]');
    if (t2) TOAST = JSON.parse(t2.textContent);
  } catch {}
  /* A3 · arrival-strip + share copy strings ({ template with [name], keep, reset, copied }). */
  let ARRIVAL = null;
  try {
    const a = document.querySelector('script[data-jp-persona-arrival]');
    if (a) ARRIVAL = JSON.parse(a.textContent);
  } catch {}

  /* S21 · persona toast — feedback when the tuned bar is off-screen.
     Bottom-centre, persona-coloured dot, 2.6s, reduced-motion = no fade. */
  let toastEl = null;
  let toastTimer = null;
  /* Render the toast. Two call styles:
       showToast('salon')                -> persona feedback (default tone)
       showToast('Message text', 'err')  -> generic err/warn tone; first arg is
                                            the literal message when a type is given.
     type: undefined (default persona style) | 'err' | 'warn'. err is assertive
     (role=alert), warn/default stay polite (role=status). Tone classes/roles are
     reset every call so a reused element never carries a stale tone. */
  const showToast = (p, type) => {
    let msg;
    if (type === 'err' || type === 'warn') {
      msg = p;
    } else {
      if (!TOAST) return;
      msg = p === 'all' ? TOAST.all : (TOAST.template || '').replace('[name]', LABELS[p] || p);
    }
    if (!msg) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.innerHTML = '<span class="dot" aria-hidden="true"></span><span class="t"></span>';
      document.body.appendChild(toastEl);
    }
    /* reset tone every call */
    toastEl.className = 'jp-ptoast' + (type === 'err' ? ' jp-ptoast--err' : type === 'warn' ? ' jp-ptoast--warn' : '');
    const assertive = type === 'err';
    toastEl.setAttribute('role', assertive ? 'alert' : 'status');
    toastEl.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
    toastEl.querySelector('.t').textContent = msg;
    clearTimeout(toastTimer);
    /* restart the entrance when re-triggered quickly */
    toastEl.classList.remove('is-shown');
    void toastEl.offsetWidth;
    toastEl.classList.add('is-shown');
    toastTimer = setTimeout(() => toastEl.classList.remove('is-shown'), 2600);
  };
  const CPC = {
    all: { cuts: 40, years: 10 }, salon: { cuts: 30, years: 12 },
    barber: { cuts: 45, years: 10 }, student: { cuts: 15, years: 8 },
  };

  const get = () => {
    const store = privacy();
    const v = store ? store.getPreference(KEY) : null;
    return VALID.includes(v) ? v : 'all';
  };

  /* 8c · tuned-bar collapse — after 4s or the first scroll the bar shrinks to a whisper (instant class
     swap + a short fade in CSS, never a max-height animation). Re-expands on each persona change. */
  let tunedTimer = null;
  let pendingPersona = null;
  let previewPersona = null;
  let previewPrevious = null;
  const collapseTuned = () => {
    const b = document.querySelector('[data-jp-tunedbar]');
    if (b && b.classList.contains('is-shown')) b.classList.add('is-whisper');
  };
  const armTunedCollapse = () => {
    const b = document.querySelector('[data-jp-tunedbar]');
    if (!b) return;
    b.classList.remove('is-whisper'); /* re-expand for this persona */
    if (tunedTimer) clearTimeout(tunedTimer);
    tunedTimer = setTimeout(collapseTuned, 4000);
  };

  let activePersona = 'all';
  const apply = (p) => {
    activePersona = p;
    VALID.forEach((v) => document.body.classList.toggle('persona-' + v, v === p));
    document.querySelectorAll('[data-jp-persona-label]').forEach((el) => { el.textContent = LABELS[p]; });
    document.querySelectorAll('[data-jp-persona-context]').forEach((el) => { el.hidden = p === 'all'; });
    document.querySelectorAll('[data-jp-persona-set]').forEach((el) => {
      const active = el.dataset.jpPersonaSet === p;
      el.removeAttribute('aria-current');
      if (el.matches('button, [role="button"]')) el.setAttribute('aria-pressed', active ? 'true' : 'false');
      el.classList.toggle('is-active', active);
    });

    /* tuned bar — slides in for an active persona, collapses for ALL.
       Drop [hidden] (no-JS default) so the max-height transition can run. */
    const tuned = document.querySelector('[data-jp-tunedbar]');
    if (tuned) {
      tuned.removeAttribute('hidden');
      const active = p !== 'all';
      tuned.classList.toggle('is-shown', active);
      if (active) {
        const who = tuned.querySelector('[data-jp-tuned-who]');
        const what = tuned.querySelector('[data-jp-tuned-what]');
        if (who) who.textContent = ((tuned.dataset.tunedPrefix || '') + ' ' + (LABELS[p] || p)).trim();
        if (what) what.textContent = TUNED[p] || '';
        armTunedCollapse();
      } else {
        tuned.classList.remove('is-whisper'); /* keep the class list consistent with the hidden state */
        if (tunedTimer) clearTimeout(tunedTimer);
      }
    }

    /* float persona-matched items first (testimonials + UGC tiles + category tiles) */
    document.querySelectorAll('.jp-test[data-pro], .jp-ugc__tile[data-pro], .jp-cat[data-pro]').forEach((el) => {
      const match = p !== 'all' && el.dataset.pro === p;
      el.classList.toggle('is-match', match);
      /* category tiles keep DOM order so keyboard/reading order tracks visual — the .jp-cat__pin marks the match; only testimonials/UGC float */
      /* only testimonials float via order; category + UGC tiles keep DOM order so focus/reading order tracks visual (they mark the match via .is-match/.jp-cat__pin) */
      if (el.classList.contains('jp-test')) el.style.order = match ? '0' : '1';
      else el.style.order = '';
    });

    /* persona size hint (drawer + category rail) + recommended-range chips */
    const hint = SIZE[p] || SIZE.all;
    if (hint) document.querySelectorAll('[data-jp-sizehint]').forEach((el) => { el.textContent = hint; });
    document.querySelectorAll('.jp-size[data-rec], .sizechip[data-rec], .jp-ruler__row[data-rec]').forEach((el) => {
      const recs = (el.dataset.rec || '').split(',').map((s) => s.trim()).filter(Boolean);
      el.classList.toggle('is-rec', p !== 'all' && recs.indexOf(p) !== -1);
    });

    /* cost-per-cut calculator preset for this chair */
    const preset = CPC[p];
    if (preset) {
      document.querySelectorAll('[data-jp-cpc]').forEach((root) => {
        if (root.dataset.personaPresets !== 'true') return;
        const cuts = root.querySelector('[data-cpc-cuts]');
        const years = root.querySelector('[data-cpc-years]');
        if (cuts) { cuts.value = preset.cuts; cuts.dispatchEvent(new Event('input', { bubbles: true })); }
        if (years) { years.value = preset.years; years.dispatchEvent(new Event('input', { bubbles: true })); }
      });
    }
  };

  const set = (p, source, options) => {
    if (!VALID.includes(p)) return false;
    const persist = !options || options.persist !== false;
    const changed = p !== activePersona;
    const store = privacy();
    if (persist && store) {
      store.setPreference(KEY, p);
      const state = typeof store.getState === 'function' ? store.getState() : null;
      pendingPersona = state && state.preferences ? null : p;
    }
    if (persist) {
      previewPersona = null;
      previewPrevious = null;
    }
    apply(p);
    document.dispatchEvent(new CustomEvent('persona:changed', { detail: { persona: p, source: source || 'user' } }));
    /* S33 §2b · analytics — only on a genuine change, so re-selecting the same persona isn't counted. Non-blocking. */
    if (changed) { try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_persona_changed', persona: p, source: source || 'user' } })); } catch (e) { /* no-op */ } }
    if (changed && source !== 'url') showToast(p); /* url arrivals get the arrival strip instead of a toast */
    return changed;
  };

  /* A3 · shareable deep links — ?for=<persona> (or ?persona=) tunes the store on arrival, then the param
     is stripped so canonical URLs stay clean. Also builds the share URL for the copy rows. */
  const readUrlPersona = () => {
    try {
      const q = new URLSearchParams(window.location.search);
      const raw = (q.get('for') || q.get('persona') || '').toLowerCase().trim();
      return VALID.indexOf(raw) !== -1 ? raw : null;
    } catch { return null; }
  };
  const stripUrlPersona = () => {
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.has('for') || u.searchParams.has('persona')) {
        u.searchParams.delete('for'); u.searchParams.delete('persona');
        history.replaceState(null, '', u.pathname + u.search + u.hash);
      }
    } catch {}
  };
  const shareUrl = (p) => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('for'); u.searchParams.delete('persona');
      if (p && p !== 'all') u.searchParams.set('for', p);
      return u.origin + u.pathname + u.search;
    } catch { return window.location.href; }
  };
  const refreshShare = () => {
    const url = shareUrl(activePersona);
    document.querySelectorAll('[data-jp-share-url]').forEach((el) => {
      if (el.tagName === 'INPUT') el.value = url; else el.textContent = url;
    });
  };

  window.JapanScissors = window.JapanScissors || {};
  window.JapanScissors.persona = { get: () => activePersona, set, refresh: () => apply(activePersona) };
  /* Generic toast for other jp scripts: JapanScissors.toast('Message', 'err'|'warn'). */
  window.JapanScissors.toast = (message, type) => showToast(message, type === 'err' || type === 'warn' ? type : 'warn');

  /* 10d · fire the conversion event SERVER-AUTHORITATIVELY — off the success marker the form renders after a
     real opt-in (not off every submit attempt, which would count server-rejected/duplicate emails). */
  const trackNewsletterSuccess = () => {
    if (window.__jpNewsSuccessFired) return;
    if (!document.querySelector('[data-jp-news-success]')) return;
    window.__jpNewsSuccessFired = true;
    try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_newsletter_subscribed', persona: get() } })); } catch {}
  };

  /* 5b/5c · "Show my range" tap (drawer + search overlay) — fire before navigation (jp:track is synchronous).
     Source is derived from the enclosing layer so one handler covers both surfaces. */
  document.addEventListener('click', (e) => {
    const rl = e.target.closest('[data-jp-range-tap]');
    if (!rl) return;
    const src = rl.closest('[data-jp-layer="search"]') ? 'search' : 'drawer';
    try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_range_tap', persona: rl.dataset.jpRangeTap, source: src } })); } catch {}
  });

  /* delegated: any [data-jp-persona-set] anywhere, now or injected later */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-jp-persona-set]');
    if (!btn) return;
    /* S33 §2b · derive the analytics source from the enclosing surface so jp_persona_changed
       (fired inside set()) carries where the switch happened: pill popover · mobile drawer · a
       persona card/tile elsewhere. Pure inference — never blocks the switch. */
    const source = btn.closest('[data-jp-persona]') ? 'pill' : btn.closest('.jp-drawer') ? 'drawer' : btn.closest('[data-jp-tunedbar]') ? 'tunedbar' : 'card';
    set(btn.dataset.jpPersonaSet, source);
    const root = btn.closest('[data-jp-persona]');
    if (root) {
      const menu = root.querySelector('.jp-persona__menu');
      const pill = root.querySelector('.jp-persona-pill');
      if (menu) menu.hidden = true;
      if (pill) { pill.setAttribute('aria-expanded', 'false'); pill.focus(); }
      root.classList.remove('is-open');
    }
  });

  /* pill dropdown — idempotent so a Customiser section reload re-wires the
     freshly injected pill (else the popover goes dead after an edit). */
  const closeDropdown = (root) => {
    const menu = root.querySelector('.jp-persona__menu');
    const pill = root.querySelector('.jp-persona-pill');
    if (menu) menu.hidden = true;
    if (pill) pill.setAttribute('aria-expanded', 'false');
    root.classList.remove('is-open');
  };
  const bindDropdowns = () => {
    document.querySelectorAll('[data-jp-persona]').forEach((root) => {
      if (root.__jpPersonaBound) return;
      const pill = root.querySelector('.jp-persona-pill');
      const menu = root.querySelector('.jp-persona__menu');
      if (!pill || !menu) return;
      root.__jpPersonaBound = true;
      if (!menu.id) menu.id = 'jp-persona-menu-' + (Math.random().toString(36).slice(2, 8));
      pill.setAttribute('aria-controls', menu.id);
      pill.addEventListener('click', (e) => {
        e.stopPropagation();
        const opening = menu.hidden;
        /* mutual exclusion: opening the persona popover collapses any open mega */
        if (opening) document.querySelectorAll('.jp-nav__item.is-open').forEach((i) => {
          i.classList.remove('is-open');
          const trigger = i.querySelector('[aria-expanded="true"]');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        });
        menu.hidden = !opening;
        pill.setAttribute('aria-expanded', opening ? 'true' : 'false');
        root.classList.toggle('is-open', opening);
      });
      root.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeDropdown(root); pill.focus(); } });
    });
  };

  /* A3 · arrival strip — shown once per session for a reversible ?for= preview.
     "Keep it" persists; "Full range" restores the preference that preceded the link. */
  const showArrival = (p) => {
    if (!ARRIVAL || p === 'all') return;
    const store = privacy();
    if (store && store.getSessionPreference('jp_persona_url_ack')) return;
    if (document.querySelector('.jp-arrival')) return;
    const strip = document.createElement('div');
    strip.className = 'jp-arrival';
    strip.setAttribute('role', 'status');
    strip.innerHTML =
      '<span class="jp-arrival__dot" aria-hidden="true"></span>' +
      '<p class="jp-arrival__t"></p>' +
      '<div class="jp-arrival__act">' +
        '<button type="button" class="jp-arrival__keep" data-jp-arrival-keep></button>' +
        '<button type="button" class="jp-arrival__reset" data-jp-arrival-reset></button>' +
      '</div>';
    strip.querySelector('.jp-arrival__keep').textContent = ARRIVAL.keep || 'Keep it';
    strip.querySelector('.jp-arrival__reset').textContent = ARRIVAL.reset || 'Full range';
    document.body.appendChild(strip);
    /* Populate the role=status region AFTER it is in the DOM so it is announced (an already-full region on
       insert may be skipped). Emphasise the persona name in a real <b> so .jp-arrival__t b applies. */
    requestAnimationFrame(() => {
      const t = strip.querySelector('.jp-arrival__t');
      const tmpl = ARRIVAL.template || '';
      const name = LABELS[p] || p;
      const i = tmpl.indexOf('[name]');
      if (i === -1) { t.textContent = tmpl; }
      else {
        t.textContent = '';
        t.appendChild(document.createTextNode(tmpl.slice(0, i)));
        const b = document.createElement('b'); b.textContent = name; t.appendChild(b);
        t.appendChild(document.createTextNode(tmpl.slice(i + 6)));
      }
      strip.classList.add('is-shown');
    });
    if (store) store.setSessionPreference('jp_persona_url_ack', '1');
  };

  /* delegated: share-link copy + arrival dismiss */
  document.addEventListener('click', (e) => {
    const copyBtn = e.target.closest('[data-jp-share-copy]');
    if (copyBtn) {
      e.preventDefault();
      const url = shareUrl(get());
      const ok = () => showToast((ARRIVAL && ARRIVAL.copied) || 'Link copied', 'warn');
      /* legacy fallback for http / older browsers where navigator.clipboard is unavailable */
      const legacy = () => {
        try {
          const ta = document.createElement('textarea');
          ta.value = url; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.left = '-9999px';
          document.body.appendChild(ta); ta.select();
          const okc = document.execCommand('copy'); ta.remove(); return okc;
        } catch { return false; }
      };
      /* only claim "copied" on genuine success — never on a rejected/blocked write */
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(ok, () => { if (legacy()) ok(); });
      } else if (legacy()) { ok(); }
      return;
    }
    const keepArrival = e.target.closest('[data-jp-arrival-keep]');
    const resetArrival = e.target.closest('[data-jp-arrival-reset]');
    if (keepArrival && previewPersona) {
      set(previewPersona, 'url-keep');
    } else if (resetArrival && previewPersona) {
      const restore = VALID.includes(previewPrevious) ? previewPrevious : 'all';
      const changed = restore !== activePersona;
      apply(restore);
      if (changed) document.dispatchEvent(new CustomEvent('persona:changed', { detail: { persona: restore, source: 'url-reset' } }));
      previewPersona = null;
      previewPrevious = null;
    }
    if (keepArrival || resetArrival || e.target.closest('[data-jp-arrival-dismiss]')) {
      const s = document.querySelector('.jp-arrival');
      if (s) { s.classList.remove('is-shown'); setTimeout(() => s.remove(), 200); }
    }
  });

  /* 8c · first scroll collapses the tuned bar to a whisper (idempotent; re-armed on each persona change). */
  window.addEventListener('scroll', collapseTuned, { passive: true });

  /* Consent can arrive after the deferred controllers initialize, or be revoked
     while the page is open. Re-read through the privacy boundary and return all
     persona surfaces to their neutral state when stored preferences are purged. */
  document.addEventListener('jp:privacy:change', (event) => {
    const detail = event.detail || {};
    if (previewPersona && activePersona === previewPersona) {
      apply(previewPersona);
      refreshShare();
      return;
    }
    let next = get();
    if (detail.preferences === true && pendingPersona && VALID.includes(pendingPersona)) {
      const store = privacy();
      if (store) store.setPreference(KEY, pendingPersona);
      next = pendingPersona;
      pendingPersona = null;
    } else if (detail.preferences === false && detail.previous && detail.previous.preferences === true) {
      pendingPersona = null;
      next = 'all';
    } else if (detail.preferences === false && pendingPersona && VALID.includes(pendingPersona)) {
      next = pendingPersona;
    }
    const changed = next !== activePersona;
    apply(next);
    refreshShare();
    if (next === 'all') {
      const arrival = document.querySelector('.jp-arrival');
      if (arrival) arrival.remove();
    }
    if (changed) document.dispatchEvent(new CustomEvent('persona:changed', { detail: { persona: next, source: 'privacy' } }));
  });

  document.addEventListener('DOMContentLoaded', () => {
    /* A3: a ?for= param is a reversible preview. Only "Keep it" persists it. */
    const urlP = readUrlPersona();
    if (urlP) {
      previewPrevious = get();
      previewPersona = urlP;
      const changed = set(urlP, 'url', { persist: false });
      stripUrlPersona();
      if (changed) showArrival(urlP);
    } else {
      apply(get());
    }
    bindDropdowns();
    refreshShare();
    trackNewsletterSuccess();
    document.addEventListener('persona:changed', refreshShare);
    /* re-apply + re-bind when the customizer injects/reloads a section */
    document.addEventListener('shopify:section:load', () => { apply(activePersona); bindDropdowns(); refreshShare(); trackNewsletterSuccess(); });
  });

  /* one document-level click-outside for every persona popover (now or injected).
     Pill clicks stopPropagation, so this only fires for clicks elsewhere. */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-jp-persona]')) return;
    document.querySelectorAll('[data-jp-persona].is-open').forEach(closeDropdown);
  });
})();
